import { useCallback, useEffect, useState } from 'react';
import useAuth from './useAuth';

export interface Result {
  type: ResultType;
  text: string;
  link: string;
}

export enum ResultType {
  'customer',
  'ticket',
  'contact',
  'employee',
}

const useSearch = (query) => {
  const [results, setResults] = useState<Record<string, Result[]>>({
    customers: [],
    tickets: [],
    contacts: [],
    employees: [],
  });
  const [loading, setLoading] = useState(false);
  const { fetch } = useAuth();

  useEffect(() => {
    refreshResults(query);
  }, [query]);

  const reset = () => {
    setResults({
      customers: [],
      tickets: [],
      contacts: [],
      employees: [],
    });
  };

  const refreshResults = useCallback(
    (query: string) => {
      setLoading(true);

      let queryURL = '/api/searchBar/';
      const graphQL =
        '{ customers(search: "' +
        query +
        '" ){ id, installationSite{ socialReason } }, ' +
        'contacts(search: "' +
        query +
        '" ){ id, name, lastname }, ' +
        'tickets(search: "' +
        query +
        '" ){ id, subject }, ' +
        'employees(search: "' +
        query +
        '" ){ id, firstname, lastname, user{ id, username } } }';

      queryURL += encodeURIComponent(graphQL);

      fetch(queryURL, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'GET',
      })
        .then((resp) => {
          setLoading(false);
          return resp.json();
        })
        .then((data) => {
          if (data) {
            const result = data['data'] || {};

            const employees: Result[] = (result['employees'] || []).map(
              (employee): Result => ({
                type: ResultType.employee,
                link: `/dashboard/user/${employee.user.id}`,
                text: `${employee.firstname} ${employee.lastname.toUpperCase()}`,
              })
            );

            const contacts: Result[] = (result['contacts'] || []).map(
              (contact): Result => ({
                type: ResultType.contact,
                link: `/dashboard/contact/${contact.id}`,
                text: `${contact.name} ${contact.lastname.toUpperCase()}`,
              })
            );

            const customers: Result[] = (result['customers'] || []).map(
              (customer): Result => ({
                type: ResultType.customer,
                link: `/dashboard/customer/${customer.id}`,
                text: `${customer.installationSite.socialReason}`,
              })
            );

            const tickets: Result[] = (result['tickets'] || []).map(
              (ticket): Result => ({
                type: ResultType.ticket,
                link: `/dashboard/tickets/${ticket.id}`,
                text: `#${ticket.id} - ${ticket.subject}`,
              })
            );

            setResults({
              contacts,
              customers,
              tickets,
              employees,
            });
          }
        })
        .catch(() => {
          setLoading(false);
        });
    },
    [setResults, setLoading]
  );

  return {
    reset,
    loading,
    results,
  };
};

export default useSearch;
